import { useI18n } from 'next-localization';
import AbrdnSelect from 'components/Generic/Forms/FormFields/AbrdnSelect';
import BaseSkeleton from 'components/Generic/BaseSkeleton';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import useHelpers from 'hooks/helpers';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import {
  useFetchPremiumBarChartDataQuery,
  useLazyFetchPremiumBarChartDataQuery,
} from '@/store/fundDetails/services/fundDetailsApi';
import { useEffect, useMemo, useState } from 'react';
import FundDetailsPremiumDiscountBarChart from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsPremiumDiscountTab/FundDetailsPremiumDiscountBarChart';
import FundDetailsPremiumDiscountBarChartCurrentDataTable from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsPremiumDiscountTab/FundDetailsPremiumDiscountBarChartCurrentDataTable';
import FundDetailsPremiumDiscountBarChartHistoricalDataTable from 'components/Pages/FundsCenter/FundDetails/FirstLevelTabs/FundDetailsPremiumDiscountTab/FundDetailsPremiumDiscountBarChartHistoricalDataTable';

type PeriodSelectorData = {
  label: string;
  selected: boolean;
  value: string;
};

const FundDetailsPremiumDiscountBarChartSection = ({ className }: string) => {
  const { t } = useI18n();
  const { language, shareClass } = usePayload();
  const { getErrorDictionary } = useHelpers();
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [chartData, setChartData] = useState([]);

  const { data, isFetching, error } = useFetchPremiumBarChartDataQuery({ language, shareClass });
  const [
    fetchFilteredData,
    { data: filteredData, isFetching: isFilteredDataFetching, error: filteredDataError },
  ] = useLazyFetchPremiumBarChartDataQuery();

  const periodSelectorData: Array<PeriodSelectorData> = useMemo(() => {
    if (!data) return [];

    return data.quarters.map((quarter) => ({
      value: quarter.period,
      label: `${quarter.periodStartDate} - ${quarter.periodEndDate}`,
      selected: quarter.period === selectedPeriod,
    }));
  }, [data, selectedPeriod]);

  const handleChange = (value: string): void => {
    setSelectedPeriod(value);
    fetchFilteredData({ language, shareClass, quarter: value });
  };

  const defaultOption: PeriodSelectorData = useMemo(() => {
    return periodSelectorData.find((option) => option.selected);
  }, [periodSelectorData]);

  useEffect(() => {
    if (data) {
      setChartData(data);
      setSelectedPeriod(data.quarters?.[0].period);
    }
  }, [data]);

  useEffect(() => {
    if (filteredData) {
      setChartData(filteredData);
    }
  }, [filteredData]);

  return (
    <div className={className}>
      <h3>{t('fundDetailsPremiumDiscountChartDataTitle')}</h3>
      {error || filteredDataError ? (
        <AlertMessageBase
          type="error"
          title={t(getErrorDictionary(error || filteredDataError))}
          className="mt-4"
        />
      ) : (
        <>
          <div className="mt-4">
            <h4>{t('fundDetailsPremiumDiscountChartTitle')}</h4>
            {isFetching || isFilteredDataFetching ? (
              <>
                <BaseSkeleton width="85px" height="16px" />
                <BaseSkeleton height="48px" />
              </>
            ) : (
              <>
                <div className="mt-4 row">
                  <div className="col-12 col-lg-4">
                    <div className="d-flex flex-column gap-1">
                      <div className="form-label text-nowrap">{t('selectPeriod')}</div>
                      <AbrdnSelect
                        key={defaultOption}
                        options={periodSelectorData}
                        defaultValue={defaultOption}
                        className="dropdown"
                        onChange={({ value }) => handleChange(value)}
                      />
                    </div>
                  </div>
                </div>
                <FundDetailsPremiumDiscountBarChart
                  data={chartData?.premiumDiscountChart}
                  className="mt-4"
                />
              </>
            )}
          </div>

          <div className="mt-8">
            <h4>{t('fundDetailsPremiumDiscountCurrentTitle')}</h4>
            <FundDetailsPremiumDiscountBarChartCurrentDataTable
              data={chartData?.premiumDiscountTable}
              isFetching={isFetching || isFilteredDataFetching}
              className="mt-4"
            />
          </div>

          <div className="mt-8">
            <h4>{t('fundDetailsPremiumDiscountHistoricalTitle')}</h4>
            <FundDetailsPremiumDiscountBarChartHistoricalDataTable
              data={chartData?.priceAggregates}
              isFetching={isFetching || isFilteredDataFetching}
              className="mt-4"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default FundDetailsPremiumDiscountBarChartSection;
